import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Clock, MapPin, Users, Music, Share2, Copy, Check, Edit2, Trash2, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, fetchWithAuth } from '../services/ApiService';
import EventModal from '../components/EventModal';
import DeleteEventDialog from '../components/DeleteEventDialog';
import EventSetlistRoles from '../components/EventSetlistRoles';
import StandardPageHeader from '../components/StandardPageHeader';
import { formatDateTime, formatTime, formatDate } from '../utils/dateFormatter';

const EventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRSVPDialog, setShowRSVPDialog] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState('no-response');
  const [rsvpReason, setRsvpReason] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  // Fetch event details
  const fetchEvent = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth(`${API_URL}/events/${eventId}`);
      
      if (data.success) {
        setEvent(data.data);
        
        // Set current user's RSVP status if they have one
        if (currentUser && data.data.attendees) {
          const userAttendee = data.data.attendees.find(
            a => a.user?._id === currentUser._id || a.user === currentUser._id
          );
          if (userAttendee) {
            setRsvpStatus(userAttendee.status || 'no-response');
          }
        }
      } else {
        navigate('/events');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  // Get event type styling
  const getEventTypeStyle = (type) => {
    const styles = {
      rehearsal: 'bg-green-100 text-green-800 border-green-200',
      concert: 'bg-orange-100 text-orange-800 border-orange-200',
      recording: 'bg-purple-100 text-purple-800 border-purple-200',
      meeting: 'bg-blue-100 text-blue-800 border-blue-200',
      audition: 'bg-red-100 text-red-800 border-red-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[type] || styles.default;
  };

  // Handle RSVP submission
  const handleRSVP = async () => {
    if (!event || !currentUser) return;
    
    try {
      const response = await fetchWithAuth(`${API_URL}/events/${event._id}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: rsvpStatus,
          reason: rsvpReason
        })
      });
      
      if (response.success) {
        // Refresh event to show updated RSVP
        fetchEvent();
        setShowRSVPDialog(false);
        setRsvpReason('');
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Failed to update RSVP');
    }
  };

  // Handle event update
  const handleEventUpdate = async (updatedEvent) => {
    console.log('Event updated:', updatedEvent);
    fetchEvent();
    setShowEditModal(false);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (choice) => {
    if (!event) return;
    
    const deleteAll = choice === 'all';
    
    try {
      const url = deleteAll 
        ? `${API_URL}/events/${event._id}?deleteAll=true`
        : `${API_URL}/events/${event._id}`;
        
      const response = await fetchWithAuth(url, {
        method: 'DELETE'
      });
      
      if (response.success) {
        navigate('/events');
      } else {
        alert('Failed to delete event: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  // Copy public RSVP link
  const copyPublicRSVPLink = () => {
    if (!event?.publicRsvpToken) return;
    
    const link = `${window.location.origin}/rsvp/${event.publicRsvpToken}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    
    // Reset after 2 seconds
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Get attendees by status
  const getAttendeesByStatus = (status) => {
    const attendees = event?.attendees?.filter(a => a.status === status).map(a => ({
      name: a.user?.username || a.user?.email || 'Unknown',
      reason: a.reason
    })) || [];
    const publicRsvps = event?.publicRsvps?.filter(r => r.status === status).map(r => ({
      name: r.name,
      reason: r.reason
    })) || [];
    return [...attendees, ...publicRsvps];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <p>Event not found</p>
      </div>
    );
  }

  const isPast = new Date(event.startTime) < new Date();
  const isOwner = event.owner?._id === currentUser?._id || event.owner === currentUser?._id;
  const confirmedAttendees = getAttendeesByStatus('confirmed');
  const declinedAttendees = getAttendeesByStatus('declined');
  const maybeAttendees = getAttendeesByStatus('maybe');

  return (
    <div className="max-w-4xl mx-auto">
      {/* Standard page header with back button, title, and actions */}
      <StandardPageHeader
        title={event.title}
        backTo="/events"
        backLabel="Events"
        actions={
          <>
            {(event.recurring || event.isRecurringInstance) && (
              <div className="flex items-center gap-1">
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  {event.recurring ? 'Recurring series' : 'Part of series'}
                </span>
              </div>
            )}
            {isAuthenticated && isOwner && (
              <>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="p-2 hover:bg-muted rounded"
                  title="Edit event"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className="p-2 hover:bg-muted rounded text-red-600"
                  title="Delete event"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </>
        }
      />

      <div className="p-4">
        {/* Event details card */}
        <div className="bg-card rounded-lg p-6 border border-border mb-6">
          <div className="flex items-center gap-3 mb-4">
            <span className={`text-sm px-2 py-1 rounded border ${getEventTypeStyle(event.type)}`}>
              {event.type}
            </span>
            {event.band?.name && (
              <button
                onClick={() => navigate(`/band/${event.band._id}`)}
                className="text-sm text-primary hover:underline"
              >
                {event.band.name}
              </button>
            )}
            {isPast && (
              <span className="text-sm text-muted-foreground">(Past event)</span>
            )}
          </div>

          {/* Event details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>
                <strong>Start:</strong> {formatDateTime(event.startTime)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>
                <strong>End:</strong> {formatDateTime(event.endTime)}
              </span>
            </div>

            {event.location?.name && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <strong>Location:</strong> {event.location.name}
                  {event.location.address && (
                    <div className="text-muted-foreground">{event.location.address}</div>
                  )}
                </div>
              </div>
            )}

            {event.setlist && (
              <div className="flex items-center gap-2 text-sm">
                <Music className="w-4 h-4 text-muted-foreground" />
                <span>
                  <strong>Setlist:</strong>{' '}
                  <button
                    onClick={() => navigate(`/setlist/${event.setlist._id}`)}
                    className="text-primary hover:underline"
                  >
                    {event.setlist.name}
                    {event.setlist.sheets && ` (${event.setlist.sheets.length} songs)`}
                  </button>
                </span>
              </div>
            )}

            {event.description && (
              <div className="pt-3 border-t border-border">
                <p className="text-sm">{event.description}</p>
              </div>
            )}

            {event.recurring && event.recurrenceRule && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>
                    <strong>Recurrence:</strong> {event.recurrenceRule.frequency}
                    {event.recurrenceRule.endDate &&
                      ` until ${formatDate(event.recurrenceRule.endDate)}`
                    }
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* RSVP and Share section */}
          <div className="mt-6 pt-4 border-t border-border space-y-4">
            {/* Public RSVP Link */}
            {event.publicRsvpToken && (
              <div className="p-3 bg-muted/50 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Public RSVP Link</span>
                  </div>
                  <button
                    onClick={copyPublicRSVPLink}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="w-3 h-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with band members who don't have accounts yet
                </p>
              </div>
            )}

            {/* RSVP button and status */}
            {isAuthenticated && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(() => {
                    const userAttendee = event.attendees?.find(
                      a => a.user?._id === currentUser?._id || a.user === currentUser?._id
                    );
                    const status = userAttendee?.status || 'no-response';
                    const statusColors = {
                      'confirmed': 'bg-green-100 text-green-800 border-green-200',
                      'declined': 'bg-red-100 text-red-800 border-red-200',
                      'maybe': 'bg-yellow-100 text-yellow-800 border-yellow-200',
                      'no-response': 'bg-gray-100 text-gray-800 border-gray-200'
                    };
                    const statusLabels = {
                      'confirmed': '✓ Going',
                      'declined': '✗ Not Going',
                      'maybe': '? Maybe',
                      'no-response': 'No Response'
                    };

                    return (
                      <>
                        <span className="text-sm">Your RSVP:</span>
                        <span className={`text-sm px-2 py-1 rounded border ${statusColors[status]}`}>
                          {statusLabels[status]}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <button
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                  onClick={() => setShowRSVPDialog(true)}
                >
                  Update RSVP
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Attendees section */}
        <div className="bg-card rounded-lg p-6 border border-border">
          <h2 className="text-lg font-semibold mb-4">Attendees</h2>

          <div className="space-y-4">
            {confirmedAttendees.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-green-600 mb-2">Going ({confirmedAttendees.length})</h3>
                <div className="space-y-1">
                  {confirmedAttendees.map((attendee, idx) => (
                    <div key={idx} className="text-sm">
                      {attendee.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {declinedAttendees.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-600 mb-2">Not Going ({declinedAttendees.length})</h3>
                <div className="space-y-1">
                  {declinedAttendees.map((attendee, idx) => (
                    <div key={idx} className="text-sm">
                      {attendee.name}
                      {attendee.reason && (
                        <span className="text-muted-foreground ml-2">- {attendee.reason}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {maybeAttendees.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-yellow-600 mb-2">Maybe ({maybeAttendees.length})</h3>
                <div className="space-y-1">
                  {maybeAttendees.map((attendee, idx) => (
                    <div key={idx} className="text-sm">
                      {attendee.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {confirmedAttendees.length === 0 && declinedAttendees.length === 0 && maybeAttendees.length === 0 && (
              <p className="text-sm text-muted-foreground">No RSVPs yet</p>
            )}
          </div>
        </div>

        {/* Role Assignments Section */}
        {event.setlist && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Role Assignments
            </h2>
            <EventSetlistRoles event={event} setlist={event.setlist} />
          </div>
        )}
      </div>

      {/* Event edit modal */}
      <EventModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEventUpdate}
        eventData={event}
        isEditMode={true}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteEventDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        eventType={
          event.recurring ? 'recurring' : 
          event.isRecurringInstance ? 'recurringInstance' : 
          'single'
        }
        eventTitle={event.title}
      />

      {/* RSVP Dialog */}
      {showRSVPDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-background rounded-lg p-6 max-w-md w-full border border-border">
            <h3 className="text-lg font-semibold mb-4">RSVP for {event.title}</h3>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="confirmed"
                  checked={rsvpStatus === 'confirmed'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">✓ Yes, I'll be there</span>
              </label>
              
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="declined"
                  checked={rsvpStatus === 'declined'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">✗ No, I can't make it</span>
              </label>
              
              <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                <input
                  type="radio"
                  name="rsvpStatus"
                  value="maybe"
                  checked={rsvpStatus === 'maybe'}
                  onChange={(e) => setRsvpStatus(e.target.value)}
                  className="mr-3"
                />
                <span className="flex-1">? Maybe</span>
              </label>
            </div>
            
            {rsvpStatus === 'declined' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Reason (optional)</label>
                <textarea
                  value={rsvpReason}
                  onChange={(e) => setRsvpReason(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  rows="2"
                  placeholder="Let them know why you can't make it..."
                />
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowRSVPDialog(false);
                  setRsvpReason('');
                }}
                className="px-4 py-2 border rounded-md hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleRSVP}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Update RSVP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPage;